import clsx from 'clsx';
import React, {useMemo} from 'react';
import {Chip as MuiChip} from '@mui/material';
import type {ChipProps} from '@mui/material';
import {Icon, designSystemToken} from '@lightricks/react-design-system';
import type {IconName} from '@lightricks/react-design-system';
import styles from './Tag.module.scss';

export type TagColor =
  | 'success'
  | 'warning'
  | 'info'
  | 'complementary'
  | 'danger'
  | 'neutral';

export type TagVariant = 'filled' | 'outlined' | 'text';

export type TagProps = {
  testID?: string;
  label?: React.ReactNode;
  icon?: IconName;
  color?: TagColor;
  variant?: TagVariant;
  size?: ChipProps['size'];
};

type ColorTokens = {
  bg: string;
  border: string;
  text: string;
};

function getColorMap(): Record<TagColor, ColorTokens> {
  return {
    success: {
      bg: designSystemToken('reference.green.10'),
      border: designSystemToken('reference.green.60'),
      text: designSystemToken('reference.green.60'),
    },
    warning: {
      bg: designSystemToken('reference.yellow.10'),
      border: designSystemToken('reference.yellow.70'),
      text: designSystemToken('reference.yellow.70'),
    },
    info: {
      bg: designSystemToken('reference.purple.10'),
      border: designSystemToken('reference.purple.60'),
      text: designSystemToken('reference.purple.60'),
    },
    complementary: {
      bg: designSystemToken('reference.pink.10'),
      border: designSystemToken('reference.pink.60'),
      text: designSystemToken('reference.pink.60'),
    },
    danger: {
      bg: designSystemToken('reference.red.10'),
      border: designSystemToken('semantic.bg.danger'),
      text: designSystemToken('semantic.fg.danger'),
    },
    neutral: {
      bg: designSystemToken('semantic.bg.tertiary'),
      border: designSystemToken('semantic.bg.alpha-secondary'),
      text: designSystemToken('semantic.fg.secondary'),
    },
  };
}

function Tag({
  testID = 'tag',
  label,
  icon,
  color = 'neutral',
  variant = 'filled',
  size = 'small',
}: TagProps) {
  const colors = useMemo(getColorMap, [])[color];
  const hasLabel = !!label;

  const chipVariant = variant === 'filled' ? 'filled' : 'outlined';

  const iconElement = icon ? (
    <Icon name={icon} size="small" appearance="neutral" color={colors.text} />
  ) : undefined;

  const className = clsx(
    styles.tag,
    styles[variant],
    !hasLabel && styles.iconOnly
  );

  const cssVars = {
    '--tag-bg': colors.bg,
    '--tag-border': colors.border,
    '--tag-text': colors.text,
  } as React.CSSProperties;

  return (
    <MuiChip
      data-testid={testID}
      variant={chipVariant}
      label={label}
      icon={iconElement}
      size={size}
      className={className}
      style={cssVars}
    />
  );
}

export default Tag;
